/**
 * `action.v1`의 상수와 런타임 검증입니다. (ADR 0027 · Issue #19 · Phase B)
 *
 * 타입은 컴파일 타임에 사라집니다. **실제로 막는 것은 이 파일의 함수**이고,
 * BFF가 이미 검증했더라도 화면이 한 번 더 돌립니다 — 중간 프록시가 바꿔치기한
 * 프레임이나 미래의 서버 버전이 카드 렌더 경로에 그대로 들어오면 안 됩니다.
 *
 * 세 가지가 이 파일의 성격을 정합니다.
 * - **닫힘.** 키 집합이 정확히 일치해야 합니다. 모르는 키 하나면 항목이 탈락합니다.
 * - **자르지 않음.** 상한을 넘으면 잘라서 통과시키지 않고 거절합니다.
 * - **모호하면 전부 탈락.** 중복 id는 어느 쪽도 신원이 아니므로 둘 다 버립니다.
 *
 * `.js`인 이유는 telemetry.runtime.js와 같습니다 — Node 테스트 러너와 브라우저
 * 번들이 같은 파일을 실제로 실행해야 하고, 타입 선언은 옆의 `.d.ts`가 owns합니다.
 */

export const AI_ACTION_CONTRACT_VERSION = "action.v1";

export const AI_ACTION_LIMITS = {
  actions: 8,
  toolCalls: 32,
  evidenceRefs: 16,
  idLength: 128,
  nameLength: 128,
  textLength: 2048,
  namespaceLength: 63,
  containerNameLength: 63,
  quantityLength: 32,
  extensionBytes: 49152,
  replicasMax: 2147483647,
  revisionMax: 2147483647,
};

export const AI_ACTION_VERBS = ["RESTART_ROLLOUT", "SCALE_REPLICAS", "ROLLBACK_REVISION", "ADJUST_RESOURCES"];
export const AI_ACTION_RISKS = ["LOW", "MEDIUM", "HIGH"];
export const AI_ACTION_WORKLOAD_KINDS = ["Deployment", "StatefulSet", "DaemonSet"];
export const AI_TOOL_CALL_STATUSES = ["success", "error", "no_data"];

/**
 * 근거가 될 수 있는 **읽기 전용** 위임 조회 도구.
 * 제안 도구 자신은 여기 없습니다 — 제안이 제안을 인용하면 근거가 없는 것과 같습니다.
 */
export const AI_ACTION_EVIDENCE_TOOLS = [
  "get_workload_detail",
  "search_logs",
  "query_metric",
  "get_warning_events",
  "get_active_alerts",
];

/**
 * 동사별로 정의된 워크로드 종류.
 *
 * DaemonSet에는 replica 수가 없고(노드당 하나), 번호 revision으로 되돌릴 수 있다는
 * 근거도 이 저장소에 없습니다. 넓히는 것은 근거가 생겼을 때입니다.
 */
export const AI_ACTION_VERB_KINDS = {
  RESTART_ROLLOUT: AI_ACTION_WORKLOAD_KINDS,
  SCALE_REPLICAS: ["Deployment", "StatefulSet"],
  ROLLBACK_REVISION: ["Deployment", "StatefulSet"],
  ADJUST_RESOURCES: AI_ACTION_WORKLOAD_KINDS,
};

/** 경로·질의·마크업 어디에 실려도 안전한 식별자. */
const SAFE_ID = /^[A-Za-z0-9._-]+$/;
/** RFC 1123 label — Kubernetes namespace·컨테이너 이름. */
const RFC1123 = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
/** Kubernetes quantity의 닫힌 부분집합. 지수 표기와 부호는 없습니다. */
const QUANTITY = /^(?:\d+|\d*\.\d+)(?:m|k|M|G|T|P|E|Ki|Mi|Gi|Ti|Pi|Ei)?$/;

/**
 * C0 제어문자와 DEL이 있는지. 정규식 리터럴 대신 코드포인트로 셉니다 —
 * 보이지 않는 바이트를 소스에 적어 두면 그 자체가 나중의 사고가 됩니다.
 */
function hasControl(value) {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * **코드포인트** 개수. `.length`는 UTF-16 단위라 astral 문자(이모지·희귀 한자·
 * 수학 문자)를 두 개로 셉니다.
 *
 * 상한의 정본은 셋 다 코드포인트입니다 — Python `len`, Go `utf8.RuneCountInString`,
 * OpenAPI `maxLength`. JavaScript만 UTF-16으로 세면 같은 제안이 브라우저에서만
 * 상한을 넘어 조용히 사라집니다. 계약이 갈라지는 자리가 바로 여기입니다.
 *
 * 반복자 대신 코드 단위를 직접 셉니다. 결과가 엔진의 반복자 구현이 아니라
 * surrogate pair 규칙 하나로 결정되도록 하기 위해서입니다.
 */
function codePointLength(value) {
  let count = 0;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff && i + 1 < value.length) {
      const next = value.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) i += 1;
    }
    count += 1;
  }
  return count;
}

function bag(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : undefined;
}

/** 키 집합이 정확한지. 필수는 모두 있고, 그 밖은 선택 목록 안에만 있어야 합니다. */
function closed(value, required, optional = []) {
  for (const key of required) if (!(key in value)) return false;
  for (const key of Object.keys(value)) {
    if (!required.includes(key) && !optional.includes(key)) return false;
  }
  return true;
}

function text(value, max) {
  if (typeof value !== "string") return false;
  if (value.length < 1 || codePointLength(value) > max) return false;
  return !hasControl(value) && /\S/.test(value);
}

function identifier(value) {
  /* 문법이 ASCII만 허용하므로 코드포인트 수와 코드 단위 수가 같습니다. 그래도
     같은 단위로 세어 둡니다 — 문법이 아니라 세는 단위가 계약이기 때문입니다.
     문법 자체는 좁힌 그대로입니다(경로·질의에 그대로 실리는 값). */
  return (
    typeof value === "string" &&
    codePointLength(value) <= AI_ACTION_LIMITS.idLength &&
    SAFE_ID.test(value)
  );
}

/** JSON 정수. bool·문자열·NaN·소수는 값이 아닙니다. */
function integer(value, min, max) {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

function quantitySet(value) {
  const set = bag(value);
  if (!set || !closed(set, [], ["cpu", "memory"])) return false;
  let present = 0;
  for (const key of ["cpu", "memory"]) {
    const raw = set[key];
    if (raw === undefined) continue;
    if (typeof raw !== "string" || codePointLength(raw) > AI_ACTION_LIMITS.quantityLength) return false;
    if (raw !== raw.trim() || !QUANTITY.test(raw)) return false;
    present += 1;
  }
  return present > 0;
}

/** 동사별 파라미터. 동사가 판별자이고 그 밖의 모양은 존재하지 않습니다. */
function parameters(verb, value) {
  const params = bag(value);
  if (!params) return false;
  if (verb === "RESTART_ROLLOUT") return closed(params, []);
  if (verb === "SCALE_REPLICAS") {
    return closed(params, ["replicas"]) && integer(params.replicas, 0, AI_ACTION_LIMITS.replicasMax);
  }
  if (verb === "ROLLBACK_REVISION") {
    return closed(params, ["revision"]) && integer(params.revision, 1, AI_ACTION_LIMITS.revisionMax);
  }
  if (!closed(params, ["containerName"], ["requests", "limits"])) return false;
  const name = params.containerName;
  if (typeof name !== "string" || codePointLength(name) > AI_ACTION_LIMITS.containerNameLength) return false;
  if (!RFC1123.test(name)) return false;
  if (params.requests === undefined && params.limits === undefined) return false;
  for (const key of ["requests", "limits"]) {
    if (params[key] !== undefined && !quantitySet(params[key])) return false;
  }
  return true;
}

/** 대상. 신원은 UID이고 이름은 계약에 없습니다. */
export function isSafeActionTarget(value) {
  const target = bag(value);
  if (!target) return false;
  if (!closed(target, ["clusterId", "namespace", "workloadKind", "workloadUid"], ["podUid"])) return false;
  if (!identifier(target.clusterId) || !identifier(target.workloadUid)) return false;
  if (target.podUid !== undefined && !identifier(target.podUid)) return false;
  const namespace = target.namespace;
  if (typeof namespace !== "string" || codePointLength(namespace) > AI_ACTION_LIMITS.namespaceLength) return false;
  if (!RFC1123.test(namespace)) return false;
  return AI_ACTION_WORKLOAD_KINDS.includes(target.workloadKind);
}

/** 근거 참조 목록. 비어 있지 않고 서로 다르며 공백만인 값이 없어야 합니다. */
function evidenceRefs(value) {
  if (!Array.isArray(value)) return false;
  if (value.length < 1 || value.length > AI_ACTION_LIMITS.evidenceRefs) return false;
  const seen = new Set();
  for (const ref of value) {
    if (!text(ref, AI_ACTION_LIMITS.idLength) || seen.has(ref)) return false;
    seen.add(ref);
  }
  return true;
}

/**
 * 제안 하나의 **모양** 검증입니다.
 *
 * 근거가 실제 성공한 조회인지, 대상이 이 턴의 권한 안인지는 여기서 알 수 없습니다 —
 * 그 결합은 서버가 자기 기록으로만 판정합니다(BFF 2차 검증).
 */
export function isSafeActionProposal(value) {
  const item = bag(value);
  if (!item) return false;
  const keys = ["id", "verb", "target", "parameters", "rationale", "expectedEffect", "risk", "evidenceRefs"];
  if (!closed(item, keys)) return false;
  if (!text(item.id, AI_ACTION_LIMITS.idLength)) return false;
  if (!AI_ACTION_VERBS.includes(item.verb) || !AI_ACTION_RISKS.includes(item.risk)) return false;
  if (!text(item.rationale, AI_ACTION_LIMITS.textLength)) return false;
  if (!text(item.expectedEffect, AI_ACTION_LIMITS.textLength)) return false;
  if (!isSafeActionTarget(item.target)) return false;
  if (!AI_ACTION_VERB_KINDS[item.verb].includes(item.target.workloadKind)) return false;
  if (!parameters(item.verb, item.parameters)) return false;
  return evidenceRefs(item.evidenceRefs);
}

/** tool 호출 요약. id·name·status 뿐이며 인자·출력은 계약에 없습니다. */
export function isSafeToolCallSummary(value) {
  const item = bag(value);
  if (!item || !closed(item, ["id", "name", "status"])) return false;
  if (!text(item.id, AI_ACTION_LIMITS.idLength)) return false;
  if (!text(item.name, AI_ACTION_LIMITS.nameLength)) return false;
  return AI_TOOL_CALL_STATUSES.includes(item.status);
}

/**
 * 인용해도 되는 호출 id 집합.
 *
 * **성공한** 위임 조회만입니다. 실패나 `no_data`는 근거가 아니고, 제안 도구는
 * 목록에 없으므로 제안이 제안을 인용할 수 없습니다. 중복 id는 신원이 아니라
 * 모호함이므로 양쪽 모두 빠집니다.
 */
export function evidenceLedger(value) {
  const eligible = new Set();
  if (!Array.isArray(value) || value.length > AI_ACTION_LIMITS.toolCalls) return eligible;
  const seen = new Set();
  const ambiguous = new Set();
  for (const item of value) {
    if (!isSafeToolCallSummary(item)) continue;
    if (seen.has(item.id)) {
      ambiguous.add(item.id);
      continue;
    }
    seen.add(item.id);
    if (item.status !== "success") continue;
    if (!AI_ACTION_EVIDENCE_TOOLS.includes(item.name)) continue;
    eligible.add(item.id);
  }
  for (const id of ambiguous) eligible.delete(id);
  return eligible;
}

/**
 * 카드가 될 수 있는 제안만 남깁니다.
 *
 * `eligible`을 주면 근거가 그 집합 안이어야 합니다(서버 경로). 화면은 서버가 이미
 * 결합을 끝냈으므로 모양만 다시 봅니다. 어느 쪽이든 **통과하지 못한 제안은 카드가
 * 아니라 사라집니다** — 답변 본문은 그대로 남습니다.
 *
 * 비용은 O(제안 수 x 근거 수)이고 둘 다 상한이 있어 유계입니다.
 */
export function safeActions(value, eligible) {
  if (!Array.isArray(value) || value.length > AI_ACTION_LIMITS.actions) return [];
  const accepted = [];
  const counts = new Map();
  for (const item of value) {
    if (!isSafeActionProposal(item)) continue;
    if (eligible && item.evidenceRefs.some((ref) => !eligible.has(ref))) continue;
    counts.set(item.id, (counts.get(item.id) ?? 0) + 1);
    accepted.push(item);
  }
  return accepted.filter((item) => counts.get(item.id) === 1);
}
